import { useEffect, useState } from "react";
import {
  BacktestJobSummary,
  ExpectancyCurve,
  IntelligenceBucket,
  IntelligenceCorrelation,
  IntelligenceOutlook,
  IntelligencePair,
  IntelligencePayload,
  IntelligenceSummary,
  api,
  isError,
} from "../api";

/** Transition Plan v1.0 lever L15 slices 1 + 2 — Trading Opportunity Intelligence.
 *
 * Per SRS FR-124 sample-size gating: every analytic shows the data we have
 * AND the threshold required for the number to be trustworthy. Below the
 * threshold, the cell is rendered greyed-out so a 100%-on-Tuesday from
 * one trade doesn't look like a Tuesday edge.
 *
 * Slice 2 adds a source selector: "Live" reads real trades, "Backtest" reads
 * a chosen backtest run's simulated trades — so the panel has signal to show
 * before 50 real trades accumulate. Backtest analytics are strategy-mechanical,
 * NOT behavioural; the live source remains the one that surfaces "I lose on
 * Mondays" type effects.
 */
export function IntelligencePanel() {
  const [data, setData] = useState<IntelligencePayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [source, setSource] = useState<"live" | "backtest">("live");
  const [jobs, setJobs] = useState<BacktestJobSummary[]>([]);
  const [jobId, setJobId] = useState<number | null>(null);
  const [curve, setCurve] = useState<ExpectancyCurve | null>(null);

  // Load completed backtest jobs for the picker (once).
  useEffect(() => {
    api.backtestResults().then((r) => {
      if (isError(r)) return;
      const done = r.jobs.filter((j) => j.status === "completed");
      setJobs(done);
      if (done.length > 0 && jobId === null) setJobId(done[0].id);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const jid = source === "backtest" ? (jobId ?? undefined) : undefined;
      const [r, c] = await Promise.all([
        api.intelligence(source, jid),
        api.expectancyCurve(source, jid, 20),
      ]);
      if (cancelled) return;
      if (isError(r)) {
        setErr(r.error);
      } else {
        setData(r);
        setErr(null);
      }
      setCurve(isError(c) ? null : c);
    }
    load();
    const id = setInterval(load, 30_000); // analytics don't change often
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [source, jobId]);

  if (err) {
    return (
      <section className="bg-surface border border-border rounded p-4">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted mb-2">
          Opportunity Intelligence
        </h2>
        <p className="text-danger font-mono text-xs">{err}</p>
      </section>
    );
  }

  if (data === null) {
    return (
      <section className="bg-surface border border-border rounded p-4">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted mb-2">
          Opportunity Intelligence
        </h2>
        <p className="text-fg-muted text-xs font-mono">loading…</p>
      </section>
    );
  }

  return (
    <section className="bg-surface border border-border rounded p-4 flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Opportunity Intelligence
          <span className="text-fg-muted normal-case tracking-normal ml-2 text-xs">
            (L15 · FR-124 sample-gated)
          </span>
        </h2>
        <SummaryBadge summary={data.summary} />
      </div>

      {/* ---- source selector (slice 2) ---- */}
      <div className="flex items-center gap-3 text-xs">
        <div className="inline-flex rounded border border-border overflow-hidden">
          <button
            onClick={() => setSource("live")}
            className={
              "px-3 py-1 font-mono uppercase " +
              (source === "live"
                ? "bg-success/20 text-success"
                : "bg-surface-2 text-fg-muted hover:text-fg")
            }
          >
            live
          </button>
          <button
            onClick={() => setSource("backtest")}
            disabled={jobs.length === 0}
            className={
              "px-3 py-1 font-mono uppercase disabled:opacity-40 " +
              (source === "backtest"
                ? "bg-success/20 text-success"
                : "bg-surface-2 text-fg-muted hover:text-fg")
            }
          >
            backtest
          </button>
        </div>
        {source === "backtest" && (
          <select
            value={jobId ?? ""}
            onChange={(e) => setJobId(Number(e.target.value))}
            className="bg-page border border-border rounded px-2 py-1 font-mono"
          >
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                #{j.id} {j.strategy_name} {j.symbol} ({j.bars_count} bars)
              </option>
            ))}
          </select>
        )}
        {source === "backtest" && (
          <span className="text-fg-muted">
            strategy-mechanical, not behavioural — live source shows your edge
          </span>
        )}
      </div>

      {data.outlook && <OutlookBanner outlook={data.outlook} />}
      {curve && curve.points.length >= 2 && <ExpectancyCurveChart curve={curve} />}
      <DayOfWeekHeatmap buckets={data.day_of_week} />
      <HourOfDayBar buckets={data.hour_of_day} />
      <PairScoreboard pairs={data.pair_scoreboard} />
      <CorrelationMatrix cells={data.correlation} />
    </section>
  );
}

// ---- summary badge --------------------------------------------------

function SummaryBadge({ summary }: { summary: IntelligenceSummary }) {
  const tradesPct = Math.min(
    100,
    Math.round((summary.closed_trades / summary.threshold_trades) * 100),
  );
  const daysPct = Math.min(
    100,
    Math.round((summary.days_of_history / summary.threshold_days) * 100),
  );
  return (
    <div className="text-xs font-mono text-fg-muted flex gap-3">
      <span>
        trades{" "}
        <span className={tradesPct >= 100 ? "text-success" : "text-fg-muted"}>
          {summary.closed_trades}/{summary.threshold_trades}
        </span>
      </span>
      <span>
        days{" "}
        <span className={daysPct >= 100 ? "text-success" : "text-fg-muted"}>
          {summary.days_of_history}/{summary.threshold_days}
        </span>
      </span>
      <span
        className={
          summary.trigger_fired
            ? "text-success font-bold uppercase"
            : "text-fg-muted uppercase"
        }
      >
        {summary.trigger_fired ? "trigger fired" : "below threshold"}
      </span>
    </div>
  );
}

// ---- daily outlook (slice 3) ----------------------------------------

function OutlookBanner({ outlook }: { outlook: IntelligenceOutlook }) {
  if (!outlook.enough_data) {
    return (
      <div className="bg-page border border-border rounded p-3 text-xs text-fg-muted">
        <span className="uppercase tracking-wider text-fg-muted">Today's outlook</span>{" "}
        — not enough history yet. Buckets light up once they clear the sample
        threshold; until then there's nothing trustworthy to surface.
      </div>
    );
  }

  // Is the current day/hour historically a good or bad window?
  const dayExp = outlook.current_day?.expectancy ?? null;
  const hourExp = outlook.current_hour?.expectancy ?? null;
  const verdict = (e: number | null): { txt: string; cls: string } => {
    if (e === null) return { txt: "no read (insufficient sample)", cls: "text-fg-muted" };
    if (e > 0) return { txt: `favourable (+${e.toFixed(2)} exp)`, cls: "text-success" };
    if (e < 0) return { txt: `unfavourable (${e.toFixed(2)} exp)`, cls: "text-danger" };
    return { txt: "flat", cls: "text-fg-muted" };
  };
  const day = verdict(dayExp);
  const hour = verdict(hourExp);

  const fmtBucket = (b: IntelligenceBucket | null) =>
    b ? `${b.bucket_key} (${b.expectancy >= 0 ? "+" : ""}${b.expectancy.toFixed(2)})` : "—";
  const fmtPair = (p: IntelligencePair | null) =>
    p ? `${p.symbol} (${p.expectancy >= 0 ? "+" : ""}${p.expectancy.toFixed(2)})` : "—";

  return (
    <div className="bg-page border border-border rounded p-3 flex flex-col gap-1">
      <div className="text-xs uppercase tracking-wider text-fg-muted">
        Today's outlook · {outlook.current_day_key} {outlook.current_hour_key}:00 UTC
      </div>
      <div className="text-xs font-mono">
        <span className="text-fg-muted">this day: </span>
        <span className={day.cls}>{day.txt}</span>
        <span className="text-fg-muted"> · </span>
        <span className="text-fg-muted">this hour: </span>
        <span className={hour.cls}>{hour.txt}</span>
      </div>
      <div className="text-[11px] font-mono text-fg-muted">
        best day {fmtBucket(outlook.best_day)} · worst {fmtBucket(outlook.worst_day)}
        {" · "}best hour {fmtBucket(outlook.best_hour)} · worst {fmtBucket(outlook.worst_hour)}
        {" · "}best pair {fmtPair(outlook.best_pair)} · worst {fmtPair(outlook.worst_pair)}
      </div>
    </div>
  );
}

// ---- expectancy curve over time (slice 4) ---------------------------

function ExpectancyCurveChart({ curve }: { curve: ExpectancyCurve }) {
  const W = 600;
  const H = 130;
  const pts = curve.points;
  const roll = pts.map((p) => p.rolling);
  const cum = pts.map((p) => p.cumulative);
  const all = [...roll, ...cum, 0]; // include zero so the baseline is in view
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const x = (i: number) => (i / (pts.length - 1)) * W;
  const y = (v: number) => H - ((v - min) / range) * H;
  const path = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const zeroY = y(0);
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Expectancy over time · rolling {curve.window} (green) vs cumulative (slate)
        · {curve.trade_count} trades
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full border border-border rounded bg-page"
        preserveAspectRatio="none"
        height={H}
      >
        <line x1={0} y1={zeroY} x2={W} y2={zeroY} stroke="#475569" strokeWidth={1} strokeDasharray="4 4" />
        <path d={path(cum)} fill="none" stroke="#94a3b8" strokeWidth={1} />
        <path d={path(roll)} fill="none" stroke="#22c55e" strokeWidth={1.5} />
      </svg>
      <p className="text-[10px] text-fg-muted mt-1">
        Rolling line trending below the cumulative = the edge is decaying
        (recent expectancy worse than the all-history average).
      </p>
    </div>
  );
}

// ---- day-of-week heatmap --------------------------------------------

function DayOfWeekHeatmap({ buckets }: { buckets: IntelligenceBucket[] }) {
  // Colour mapping: positive expectancy = green hue, negative = red, zero = slate.
  // Saturation indicates magnitude relative to the max abs in the row.
  const maxAbs = Math.max(1e-9, ...buckets.map((b) => Math.abs(b.expectancy)));
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Day of week (UTC) · expectancy per trade
      </div>
      <div className="grid grid-cols-7 gap-1">
        {buckets.map((b) => (
          <Cell key={b.bucket_key} bucket={b} maxAbs={maxAbs} />
        ))}
      </div>
    </div>
  );
}

function Cell({ bucket, maxAbs }: { bucket: IntelligenceBucket; maxAbs: number }) {
  const greyed = !bucket.sample_sufficient;
  const intensity = Math.min(1, Math.abs(bucket.expectancy) / maxAbs);
  // Inline RGBA so Tailwind doesn't have to safelist N opacity classes.
  // Colours match tailwind.config.js: ok=#22c55e, bad=#ef4444.
  const alpha =
    bucket.trade_count === 0 ? 0.1 : Math.max(0.1, Math.min(0.6, intensity * 0.6));
  const bg =
    bucket.trade_count === 0
      ? "rgba(30,41,59,1)"
      : bucket.expectancy > 0
        ? `rgba(34,197,94,${alpha})`
        : bucket.expectancy < 0
          ? `rgba(239,68,68,${alpha})`
          : "rgba(51,65,85,1)";
  return (
    <div
      style={{ backgroundColor: bg }}
      className={`${greyed ? "opacity-40" : ""} border border-border rounded p-2 text-center`}
      title={
        bucket.trade_count === 0
          ? `${bucket.bucket_key}: no trades`
          : `${bucket.bucket_key}: ${bucket.trade_count} trades, ` +
            `win ${(bucket.win_rate * 100).toFixed(0)}%, ` +
            `expectancy ${bucket.expectancy >= 0 ? "+" : ""}${bucket.expectancy.toFixed(2)}` +
            (greyed ? ` — insufficient sample (need ${bucket.threshold})` : "")
      }
    >
      <div className="text-xs font-bold text-fg-muted">{bucket.bucket_key}</div>
      <div className="text-xs font-mono text-fg">
        {bucket.trade_count === 0
          ? "—"
          : `${bucket.expectancy >= 0 ? "+" : ""}${bucket.expectancy.toFixed(1)}`}
      </div>
      <div className="text-[10px] font-mono text-fg-muted">
        n={bucket.trade_count}
      </div>
    </div>
  );
}

// ---- hour-of-day bar -----------------------------------------------

function HourOfDayBar({ buckets }: { buckets: IntelligenceBucket[] }) {
  const maxAbs = Math.max(1e-9, ...buckets.map((b) => Math.abs(b.expectancy)));
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Hour of day (UTC) · expectancy
      </div>
      <div className="grid grid-cols-12 gap-1">
        {buckets.map((b) => (
          <HourCell key={b.bucket_key} bucket={b} maxAbs={maxAbs} />
        ))}
      </div>
    </div>
  );
}

function HourCell({ bucket, maxAbs }: { bucket: IntelligenceBucket; maxAbs: number }) {
  const greyed = !bucket.sample_sufficient;
  const intensity = Math.min(1, Math.abs(bucket.expectancy) / maxAbs);
  const alpha =
    bucket.trade_count === 0 ? 0.1 : Math.max(0.1, Math.min(0.6, intensity * 0.6));
  const bg =
    bucket.trade_count === 0
      ? "rgba(30,41,59,1)"
      : bucket.expectancy > 0
        ? `rgba(34,197,94,${alpha})`
        : bucket.expectancy < 0
          ? `rgba(239,68,68,${alpha})`
          : "rgba(51,65,85,1)";
  return (
    <div
      style={{ backgroundColor: bg }}
      className={`${greyed ? "opacity-40" : ""} rounded px-1 py-1 text-center text-[10px] font-mono`}
      title={
        bucket.trade_count === 0
          ? `${bucket.bucket_key}h: no trades`
          : `${bucket.bucket_key}:00 UTC — n=${bucket.trade_count}, ` +
            `wr=${(bucket.win_rate * 100).toFixed(0)}%, ` +
            `exp=${bucket.expectancy.toFixed(2)}` +
            (greyed ? ` (insufficient: need ${bucket.threshold})` : "")
      }
    >
      <div className="text-fg-muted">{bucket.bucket_key}</div>
      <div className="text-fg">{bucket.trade_count}</div>
    </div>
  );
}

// ---- pair scoreboard -----------------------------------------------

function PairScoreboard({ pairs }: { pairs: IntelligencePair[] }) {
  if (pairs.length === 0) {
    return (
      <div>
        <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
          Pair scoreboard
        </div>
        <p className="text-fg-muted text-xs font-mono">
          no closed trades yet — table fills in as you trade.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Pair scoreboard
      </div>
      <table className="w-full text-xs font-mono">
        <thead className="text-fg-muted">
          <tr>
            <th className="text-left p-1">symbol</th>
            <th className="text-right p-1">n</th>
            <th className="text-right p-1">win%</th>
            <th className="text-right p-1">expectancy</th>
            <th className="text-right p-1">profit factor</th>
            <th className="text-right p-1">total P/L</th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((p) => (
            <tr
              key={p.symbol}
              className={p.sample_sufficient ? "" : "opacity-40"}
            >
              <td className="text-left p-1 text-fg">{p.symbol}</td>
              <td className="text-right p-1 text-fg-muted">{p.trade_count}</td>
              <td className="text-right p-1 text-fg-muted">
                {(p.win_rate * 100).toFixed(0)}
              </td>
              <td
                className={`text-right p-1 ${p.expectancy >= 0 ? "text-success" : "text-danger"}`}
              >
                {p.expectancy >= 0 ? "+" : ""}
                {p.expectancy.toFixed(2)}
              </td>
              <td className="text-right p-1 text-fg-muted">
                {p.profit_factor === null ? "∞" : p.profit_factor.toFixed(2)}
              </td>
              <td
                className={`text-right p-1 ${p.total_pnl >= 0 ? "text-success" : "text-danger"}`}
              >
                {p.total_pnl >= 0 ? "+" : ""}
                {p.total_pnl.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {pairs.some((p) => !p.sample_sufficient) && (
        <p className="text-[10px] text-fg-muted mt-1">
          Greyed rows: sample below threshold ({pairs[0].threshold} trades).
        </p>
      )}
    </div>
  );
}

// ---- correlation matrix --------------------------------------------

function CorrelationMatrix({ cells }: { cells: IntelligenceCorrelation[] }) {
  if (cells.length === 0) {
    return (
      <div>
        <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
          Pair correlation (daily P/L)
        </div>
        <p className="text-fg-muted text-xs font-mono">
          no pair meets the participation threshold yet — needs more trades per symbol.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-fg-muted mb-1">
        Pair correlation (daily P/L, Pearson)
      </div>
      <table className="text-xs font-mono">
        <thead className="text-fg-muted">
          <tr>
            <th className="text-left p-1">pair</th>
            <th className="text-right p-1">r</th>
            <th className="text-right p-1">overlap days</th>
          </tr>
        </thead>
        <tbody>
          {cells.map((c) => (
            <tr key={`${c.symbol_a}-${c.symbol_b}`}>
              <td className="text-left p-1 text-fg">
                {c.symbol_a} × {c.symbol_b}
              </td>
              <td
                className={
                  "text-right p-1 " +
                  (c.correlation === null
                    ? "text-fg-muted"
                    : Math.abs(c.correlation) >= 0.7
                      ? c.correlation > 0
                        ? "text-warn"
                        : "text-warn"
                      : "text-fg-muted")
                }
              >
                {c.correlation === null ? "—" : c.correlation.toFixed(2)}
              </td>
              <td className="text-right p-1 text-fg-muted">{c.overlap_days}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-fg-muted mt-1">
        |r| ≥ 0.7 flagged: pairs are moving together (risk concentration).
      </p>
    </div>
  );
}
